/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Renderer2,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import * as _ from 'lodash';
import {FcAgResponsiveService} from '../fc-ag-services/fc-ag-responsive.service';

export type FloatLabelType = 'always' | 'auto' | 'never';

/**
 * Firecrest Dropdown List component.
 */
@Component({
    selector: 'fc-ag-drop-down-list',
    templateUrl: './fc-ag-drop-down-list.component.html',
    styleUrls: ['./fc-ag-drop-down-list.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FcAgDropDownListComponent implements OnInit {
    /**
     * List of classname that will NOT trigger closing dropdown when click on.
     */
    readonly IGNORED_CLASSNAME_ON_DROPDOWN_BLUR = [
        'item',
        'item-label',
        'checkbox-container',
        'checkmark',
        'checkbox-input'
    ];

    /**
     * Default max count of selected item.
     * If greater than, label will be assigned by shortener.
     */
    readonly DEFAULT_MAX_ITEM: number = 2;

    /**
     * Default height of scroller in desktop mode.
     */
    readonly DEFAULT_SCROLLER_MAX_HEIGHT = 195; // in px

    /**
     * Model of search input.
     */
    searchTerm: string = '';

    /**
     * Name of selection property.
     * To determine item selected or not.
     */
    readonly SELECTED_KEY: string = '_selected';

    /**
     * To display filter input placeholder.
     */
    readonly FILTER_PLACEHOLDER: string = 'Keyword search...';

    /**
     * Getter SELECTED_KEY.
     *
     * @returns String.
     */
    get selectedKey(): string {
        return this.SELECTED_KEY;
    }

    private _items: any[] = Array();
    
    private _activeItems: any[] = Array();

    /**
     * Input of items.
     *
     * @returns Any[].
     */
    get items() {
        return this._items;
    };

    /**
     * Input Setter _items.
     */
    @Input() set items(value: any[]) {
        if (_.isNil(value)) {
            value = [];
        }
        this._items = _.cloneDeep(value);
        this.viewportItems = this._items;
        this._activeItems = this._items.filter(item => !item._disabled);

        _.forEach(this.selectedItems, (sItem) => {
            let tmpItems = _.filter(this.items,
                item => item[this.bindValue] === sItem[this.bindValue]);
            if (tmpItems.length > 0) {
                _.map(tmpItems, (ti) => {
                    ti[this.SELECTED_KEY] = true;
                });
            }
        });
    };

    private _selectedItems: any[] = Array();

    /**
     * Getter hasSelected.
     *
     * @returns Boolean.
     */
    get hasSelected(): boolean {
        return this._selectedItems.length > 0;
    }

    /**
     * Getter selectedItems.
     * Pre-selection items.
     *
     * @returns Any[].
     */
    get selectedItems() {
        return this._selectedItems;
    }

    /**
     * Input Setter _selectedItems.
     *
     * @param value Any[].
     */
    @Input() set selectedItems(value: any[]) {
        if (!value || (value && value.length === 0)) {
            value = [];
            this._items = this._items.map(item => {
                item[this.SELECTED_KEY] = false;
                return item;
            });
        }
        this._selectedItems = (value);
    };

    private _viewportItems: any[] = Array();

    /**
     * Input Setter _viewportItems.
     *
     * @param value Any[].
     */
    set viewportItems(value: any[]) {
        if (value === null) {
            value = [];
        }
        this._viewportItems = (value);
    };

    /**
     * Getter _viewportItems.
     *
     * @returns Any[].
     */
    get viewportItems(): any[] {
        return this._viewportItems;
    }

    private _isOpen: boolean = false;

    /**
     * Getter _isOpen.
     *
     * @returns Boolean.
     */
    get isDropdownOpen(): boolean {
        return this._isOpen;
    }

    /**
     * Setter _isOpen.
     */
    set isOpen(state: boolean) {
        if (!state) {
            this.panelStateChange.emit(this.selectedItems);
        }
        this._isOpen = state;
    }

    private _searchEnabled: boolean = false;

    /**
     * Getter _searchEnabled.
     * To Hide/Show search input.
     *
     * @returns Boolean.
     */
    get searchEnabled() {
        return this._searchEnabled;
    }

    /**
     * Input Setter _searchEnabled.
     * To Hide/Show search input.
     */
    @Input() set searchEnabled(visibility: boolean) {
        this._searchEnabled = visibility;
    }

    private _selectionHelper: boolean = true;

    /**
     * Input Enabled/Disabled selection helper.
     *
     * @returns Boolean.
     */
    get selectionHelper() {
        return this._selectionHelper;
    }

    /**
     * Input Setter selectionHelper.
     *
     * @param visibility Boolean.
     */
    @Input() set selectionHelper(visibility: boolean) {
        this._selectionHelper = visibility;
    }

    private _maxItemLabel: number = 2;

    /**
     * Getter Max Items to Show on Button label.
     *
     * @returns Number.
     */
    get maxItemLabel() {
        return this._maxItemLabel;
    }

    /**
     * Input Setter _maxItemLabel.
     */
    @Input() set maxItemLabel(value: number) {
        if (_.isNil(value)) {
            this._maxItemLabel = this.DEFAULT_MAX_ITEM;
        } else {
            this._maxItemLabel = value.valueOf();
        }
    }

    private _selectAllLabel: string = 'Select All';

    /**
     * Input select all label, default is 'Select All'.
     *
     * @returns String.
     */
    get selectAllLabel() {
        return this._selectAllLabel;
    };

    /**
     * Input Setter selectAllLabel.
     *
     * @param saLabel String.
     */
    @Input() set selectAllLabel(saLabel: string) {
        this._selectAllLabel = saLabel;
    }

    private _isSelectAll: boolean = false;

    /**
     * Getter to determine select all state.
     *
     * @returns Boolean.
     */
    get isSelectAll(): boolean {
        return this._isSelectAll;
    }

    private _isMobileMode: boolean = false;

    /**
     * Getter mobile mode.
     *
     * @returns String.
     */
    get isMobileMode() {
        return this._isMobileMode;
    };

    /**
     * Setter mobile mode.
     *
     * @param signal Boolean.
     */
    set isMobileMode(signal: boolean) {
        this._isMobileMode = signal;
    };

    private _itemLabel: string = 'items';

    /**
     * Input Getter ItemLabel.
     *
     * @returns String.
     */
    get itemLabel(): string {
        return this._itemLabel;
    }

    /**
     * Input Setter ItemLabel.
     *
     * @param iLabel String.
     */
    @Input() set itemLabel(iLabel: string) {
        this._itemLabel = !_.isNil(iLabel) ? iLabel : this.itemLabel;
    }

    /**
     * ElementRef of dropdown button.
     */
    @ViewChild('dropdownButton') dropdownButtonElement!: ElementRef;

    /**
     * ElementRef of dropdown panel.
     */
    @ViewChild('searchInput') searchInputElement!: ElementRef;

    /**
     * ElementRef of dropdown button label.
     */
    @ViewChild('dropdownButtonLabel') dropdownButtonLabelElement!: ElementRef;

    /**
     * ElementRef of dropdown panel.
     */
    @ViewChild('dropdownPanel') dropdownPanelElement!: ElementRef;

    /**
     * ElementRef of mobile wrapper header.
     */
    @ViewChild('mobileModeHeader') mobileModeHeaderElement!: ElementRef;

    /**
     * ElementRef of searchContainer.
     */
    @ViewChild('searchContainer') searchContainerElement!: ElementRef;

    /**
     * ElementRef of selectionHelper.
     */
    @ViewChild('selectionHelper') selectionHelperElement!: ElementRef;

    /**
     * Input of dropdown label (top label, if not set, will disappear).
     *
     * @returns String.
     */
    @Input() dropdownLabel!: string;

    /**
     * Input of dropdown placeholder.
     *
     * @returns String.
     */
    @Input() dropdownPlaceholder!: string;

    /**
     * Input of multiple selected placeholder.
     * 
     * @returns String.
     */
    @Input() multipleSelectedPlaceholder: string = '';

    /**
     * Input of binding label.
     *
     * @returns String.
     */
    @Input() bindLabel!: string;

    /**
     * Input of binding value.
     *
     * @returns String.
     */
    @Input() bindValue!: string;

    /**
     * Input of multiselect flag.
     *
     * @returns Boolean.
     */
    @Input() multiselect: boolean = false;


    /**
     * Input disable toggle single selection.
     *
     * @returns Boolean.
     */
    @Input() disableToggleSingleSelection: boolean = false;
    

    /**
     * Getter isMultiselect.
     *
     * @returns Boolean.
     */
    get isMultiselect(): boolean {
        return this.multiselect;
    }

    /**
     * Float label(always|auto|never)
     * Default is 'always' show.
     */
    @Input() floatLabel: FloatLabelType = 'always';
    
    /**
     * Input Blur to Close dropdown panel.
     * Click outside of component to close.
     */
    @Input() blurToClose: boolean = true;
    
    /**
     * Input disabled state.
     */
    @Input() disabled: boolean = false;

    /**
     * Render custom item tpl.
     */
    @Input() customItemTpl!: TemplateRef<any>|null;

    /**
     * Output Trigger Selected Items Changed event.
     * Pair with Two-Way binding.
     */
    @Output() selectedItemsChange: EventEmitter<any[]>;

    /**
     * Output to trigger on selection changed.
     */
    @Output() selectionChange: EventEmitter<any[]>;

    /**
     * Output to trigger whenever dropdown panel is closed.
     */
    @Output() panelStateChange: EventEmitter<any[]>;

    /**
     * FcAgDropDownListComponent constructor.
     *
     * @param renderer Renderer2.
     * @param fcResponsiveService FcAgResponsiveService.
     */
    constructor(private renderer: Renderer2,
                private fcResponsiveService: FcAgResponsiveService) {
        this.selectedItemsChange = new EventEmitter<any[]>();
        this.selectionChange = new EventEmitter<any[]>();
        this.panelStateChange = new EventEmitter<any[]>();
    }

    /**
     * Angular OnInit implementation.
     */
    ngOnInit() {
        this.fcResponsiveService.mediaBreakpoint$.subscribe((bp: string) => {
            this.isMobileMode = this.fcResponsiveService.isMobileMode();
            this.populateVirtualScrollerStyle();
        });

        if (this.blurToClose) {
            this.renderer.listen('window', 'click', (e: Event) => {
                if ((e.target !== this.dropdownButtonElement?.nativeElement)
                    && (e.target !== this.dropdownPanelElement?.nativeElement)
                    && (e.target !== this.dropdownButtonLabelElement?.nativeElement)
                    && (e.target !== this.searchInputElement?.nativeElement)
                    && _.intersection(Array.from((<HTMLElement>e.target).classList),
                        this.IGNORED_CLASSNAME_ON_DROPDOWN_BLUR).length < 1) {
                    this._isOpen = false;
                }
            });
        }
    }

    /**
     * Toggle Dropdown.
     */
    toggleDropdown(): void {
        if (this.disabled) {
            return;
        }
        this.isOpen = !this.isDropdownOpen;
        setTimeout(() => {
            this.searchInputElement.nativeElement.focus();
        });
    }

    /**
     * To toggle item selection.
     *
     * @param item Any.
     */
    toggleSelection(item: any): void {
        if (item._disabled) return;
        const selectedState = item[this.SELECTED_KEY];
        if (!this.multiselect) {
            const selectedState = item[this.SELECTED_KEY];
            //Prevent toggle selection for selected item.
            if (this.disableToggleSingleSelection && selectedState) {
                this.isOpen = false;
                return;
            }
            //Reset selection
            this.toggleBulkSelection(false);
            this.isOpen = false;
        }
        // Toggle selection.
        if (!_.isNil(item)) {
            item[this.SELECTED_KEY] = !selectedState;
        }
        this.updateSelectedItems();
    }

    /**
     * To handle search term changing.
     *
     * @param searchTerm Any.
     */
    searchTermChange(searchTerm: any): void {
        const tmpSearch = _.filter(this.items,
            item => {
                var lowerCaseItem = item[this.bindLabel].toLowerCase();
                var lowerCaseSearchTerm = searchTerm.toLowerCase();
                return lowerCaseItem.includes(lowerCaseSearchTerm);
            });
        this._viewportItems = _.clone(tmpSearch);
    }

    /**
     * To populate Dropdown button label.
     *
     * @returns String.
     */
    populateDropdownLabel(): string {
        let tmpLabel = '';
        const selectedCount = this.selectedItems?.length;
        if (selectedCount > 0) {
            if (this.multiselect && selectedCount === this._activeItems?.length) {
                tmpLabel = this.dropdownPlaceholder;
                this._isSelectAll = true;
            } else if (this.multiselect && selectedCount >= this.maxItemLabel) {
                if (this.multipleSelectedPlaceholder) {
                    tmpLabel = this.multipleSelectedPlaceholder;
                } else {
                    tmpLabel = `${selectedCount} ${this.itemLabel} selected`;
                }
                this._isSelectAll = false;
            } else {
                this._isSelectAll = false;
                _.each(this.selectedItems, (item: any, k: number) => {
                    tmpLabel += item[this.bindLabel] + (k < (selectedCount - 1) ? ', ' : '');
                });
            }
        } else {
            tmpLabel = this.dropdownPlaceholder;
            this._isSelectAll = false;
        }
        return tmpLabel;
    }

    /**
     * To populate dropdown tooltip in-case text overflow.
     *
     * @returns String.
     */
    populateDropdownTooltip(): string {
        let buttonTooltip = '';
        if (this.hasSelected) {
            buttonTooltip = _.map(this.selectedItems, it => it[this.bindLabel]).join(', ');
        } else {
            buttonTooltip = !_.isNil(this.dropdownPlaceholder) ? this.dropdownPlaceholder : 'Not selected';
        }
        return buttonTooltip;

    }

    /**
     * To populate scroller style based on browser context.
     *
     * @returns Object.
     */
    populateVirtualScrollerStyle(): object {
        return {
            'height': (this.items.length * 56).valueOf() + 'px',
            'max-height': (this.isMobileMode
                ? (window.innerHeight
                    - (
                        (this.mobileModeHeaderElement?.nativeElement?.offsetHeight?.valueOf() ?? 0)
                        + (this.searchContainerElement?.nativeElement?.offsetHeight?.valueOf() ?? 0)
                        + (this.selectionHelperElement?.nativeElement?.offsetHeight?.valueOf() ?? 0)
                    )
                )
                : this.DEFAULT_SCROLLER_MAX_HEIGHT) + 'px'
        };
    }

    /**
     * To handle select all function.
     * In multi-select mode.
     */
    selectAllHandler(): void {
        this._isSelectAll = !this._isSelectAll;
        this.toggleBulkSelection(this.isSelectAll);
        this.updateSelectedItems();
    }

    /**
     * To handle select none function.
     * In multi-select mode.
     */
    selectNoneHandler(): void {
        this.toggleBulkSelection(false);
        this.updateSelectedItems();
    }

    private toggleBulkSelection(state: boolean): void {
        _.map(this.items, (item) => {
            if (!item._disabled) {
                item[this.SELECTED_KEY] = state;
            }
        });
    }

    private updateSelectedItems(): void {
        this.selectedItems = _.filter(this.items, [this.SELECTED_KEY, true]);
        this.selectedItemsChange.emit(this.selectedItems);
        this.selectionChange.emit(this.selectedItems);
    }
}
